
import { useEffect, useRef, useState } from 'react';

export default function Philosophy() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-16 md:py-24 lg:py-32 bg-[#F8F6F3] overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C9A86C]/30 to-transparent" />
      <div className="absolute top-20 right-10 md:right-20 w-32 md:w-64 h-32 md:h-64 border border-[#C9A86C]/10 rounded-full hidden md:block" />
      <div className="absolute bottom-20 left-10 md:left-20 w-20 md:w-40 h-20 md:h-40 border border-[#C9A86C]/10 hidden md:block" />
      
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 lg:gap-20 items-center">
          {/* Image Side */}
          <div className={`relative transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
          }`}>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20Black%20African%20craftsman%20hands%20working%20on%20detailed%20wood%20finishing%20in%20luxury%20interior%20space%2C%20skilled%20Nigerian%20artisan%20doing%20precision%20carpentry%20work%2C%20warm%20ambient%20lighting%2C%20high%20end%20residential%20construction%2C%20editorial%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20sophisticated%20neutral%20tones&width=600&height=750&seq=phil003&orientation=portrait"
                alt="Precision craftsmanship"
                className="w-full h-[350px] sm:h-[450px] md:h-[500px] lg:h-[600px] object-cover"
              />
              <div className="absolute -bottom-3 -right-3 md:-bottom-6 md:-right-6 w-24 md:w-48 h-24 md:h-48 bg-[#C9A86C] -z-10" />
              <div className="absolute -top-3 -left-3 md:-top-6 md:-left-6 w-16 md:w-32 h-16 md:h-32 border-2 border-[#0A0A0A] -z-10" />
              
              {/* Floating Badge */}
              <div className="absolute -right-2 md:-right-8 top-1/2 -translate-y-1/2 bg-[#0A0A0A] p-4 md:p-6 shadow-2xl">
                <span className="text-3xl md:text-5xl font-serif text-[#C9A86C]">15+</span>
                <p className="text-white/60 text-xs md:text-sm mt-1 md:mt-2">Years of<br />Excellence</p>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
          }`}>
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="w-8 md:w-12 h-px bg-[#C9A86C]" />
              <span className="text-[#C9A86C] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase">Our Philosophy</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-[#0A0A0A] mb-6 md:mb-8 leading-tight">
              Execution is
              <span className="block text-[#C9A86C]">Everything</span>
            </h2>

            <div className="space-y-4 md:space-y-6 text-base md:text-lg text-[#4A4A4A] leading-relaxed">
              <p>
                BuildFast exists for clients who understand that <strong className="text-[#0A0A0A]">finishing is not an afterthought</strong>—it is the project itself.
              </p>
              
              <p>
                Every surface, every line, every material is <strong className="text-[#0A0A0A]">intentional</strong>. We approach each space as a canvas where precision meets artistry.
              </p>
              
              <p>
                We do not rush. We do not overstate. We deliver <strong className="text-[#0A0A0A]">quietly—and flawlessly</strong>.
              </p>
            </div>

            {/* Features */}
            <div className="mt-8 md:mt-12 grid grid-cols-2 gap-3 md:gap-6">
              <div className="group p-4 md:p-6 bg-white border border-[#E5E5E5] hover:border-[#C9A86C] transition-colors cursor-pointer">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#0A0A0A] flex items-center justify-center mb-3 md:mb-4 group-hover:bg-[#C9A86C] transition-colors">
                  <i className="ri-ruler-2-line text-lg md:text-xl text-white" />
                </div>
                <h4 className="font-serif text-lg md:text-xl text-[#0A0A0A] mb-1 md:mb-2">Precision</h4>
                <p className="text-xs md:text-sm text-[#6B6B6B]">Millimeter-perfect execution in every detail</p>
              </div>
              
              <div className="group p-4 md:p-6 bg-white border border-[#E5E5E5] hover:border-[#C9A86C] transition-colors cursor-pointer">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#0A0A0A] flex items-center justify-center mb-3 md:mb-4 group-hover:bg-[#C9A86C] transition-colors">
                  <i className="ri-palette-line text-lg md:text-xl text-white" />
                </div>
                <h4 className="font-serif text-lg md:text-xl text-[#0A0A0A] mb-1 md:mb-2">Artistry</h4>
                <p className="text-xs md:text-sm text-[#6B6B6B]">Where craftsmanship meets creative vision</p>
              </div>
              
              <div className="group p-4 md:p-6 bg-white border border-[#E5E5E5] hover:border-[#C9A86C] transition-colors cursor-pointer">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#0A0A0A] flex items-center justify-center mb-3 md:mb-4 group-hover:bg-[#C9A86C] transition-colors">
                  <i className="ri-shield-star-line text-lg md:text-xl text-white" />
                </div>
                <h4 className="font-serif text-lg md:text-xl text-[#0A0A0A] mb-1 md:mb-2">Quality</h4>
                <p className="text-xs md:text-sm text-[#6B6B6B]">Premium materials, uncompromising standards</p>
              </div>
              
              <div className="group p-4 md:p-6 bg-white border border-[#E5E5E5] hover:border-[#C9A86C] transition-colors cursor-pointer">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#0A0A0A] flex items-center justify-center mb-3 md:mb-4 group-hover:bg-[#C9A86C] transition-colors">
                  <i className="ri-time-line text-lg md:text-xl text-white" />
                </div>
                <h4 className="font-serif text-lg md:text-xl text-[#0A0A0A] mb-1 md:mb-2">Timeless</h4>
                <p className="text-xs md:text-sm text-[#6B6B6B]">Designs that transcend fleeting trends</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
